import React, { useState, useEffect, useCallback } from 'react';
import { useNavigation } from '../../contexts/NavigationContext';
import { createAuthRequestInit } from '../../utils/auth';
import { API_BASE } from '../../config/api';

export const AccountPage: React.FC = () => {
  const { user } = useNavigation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [companies, setCompanies] = useState<string[]>([]);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    company: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const ensureCompanyPresence = useCallback((list: string[], value?: string | null) => {
    if (!value) return list;
    return list.includes(value) ? list : [...list, value];
  }, []);

  // Fetch companies on mount (and when explicitly requested)
  const fetchCompanies = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/users/companies`, createAuthRequestInit());

      if (res.ok) {
        const json = await res.json();
        setCompanies(prev => {
          const serverCompanies = Array.isArray(json.data?.companies) ? json.data.companies : [];
          // Keep any locally ensured company (e.g., user value) that might not be returned yet
          const merged = Array.from(new Set([...serverCompanies, ...prev]));
          return ensureCompanyPresence(merged, formData.company);
        });
      }
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  }, [ensureCompanyPresence, formData.company]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Fetch user data on mount
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const res = await fetch(`${API_BASE}/users/${user.id}`, createAuthRequestInit());

        if (!res.ok) {
          throw new Error('Failed to fetch user data');
        }

        const json = await res.json();
        const userInfo = json.data.user;
        
        setUserData(userInfo);
        const normalizedCompany = userInfo.company || '';

        setFormData({
          firstName: userInfo.firstName || '',
          lastName: userInfo.lastName || '',
          email: userInfo.companyEmail || userInfo.email || '',
          phoneNumber: userInfo.contactNumber || '',
          company: normalizedCompany,
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Ensure the user's current company appears even if not returned yet
        setCompanies(prev => ensureCompanyPresence(prev, normalizedCompany));
      } catch (err: any) {
        console.error('Failed to fetch user data:', err);
        setError(err.message || 'Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!user?.id) {
      setError('User not found');
      return;
    }

    try {
      setIsSaving(true);
      // Update profile information
      const updatePayload: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyEmail: formData.email,
        contactNumber: formData.phoneNumber,
        company: formData.company || null
      };

      const updateRes = await fetch(
        `${API_BASE}/users/${user.id}`,
        createAuthRequestInit(
          {
            method: 'PUT',
            body: JSON.stringify(updatePayload)
          },
          true
        )
      );

      if (!updateRes.ok) {
        const errorData = await updateRes.json().catch(() => ({ message: 'Failed to update profile' }));
        throw new Error(errorData.message || 'Failed to update profile');
      }

      // Change password if provided
      if (formData.newPassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          throw new Error('New password and confirm password do not match');
        }

        if (!formData.currentPassword) {
          throw new Error('Current password is required to change password');
        }

        const passwordRes = await fetch(
          `${API_BASE}/users/${user.id}/change-password`,
          createAuthRequestInit(
            {
              method: 'POST',
              body: JSON.stringify({
                currentPassword: formData.currentPassword,
                newPassword: formData.newPassword
              })
            },
            true
          )
        );

        if (!passwordRes.ok) {
          const errorData = await passwordRes.json().catch(() => ({ message: 'Failed to change password' }));
          throw new Error(errorData.message || 'Failed to change password');
        }
      }

      setSuccess('Profile updated successfully');
      
      // Clear password fields
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));

      // Refresh user data
      const refreshRes = await fetch(`${API_BASE}/users/${user.id}`, createAuthRequestInit());

      if (refreshRes.ok) {
        const json = await refreshRes.json();
        const userInfo = json.data.user;
        const refreshedCompany = userInfo.company || '';
        setUserData(userInfo);
        setFormData(prev => ({
          ...prev,
          firstName: userInfo.firstName || '',
          lastName: userInfo.lastName || '',
          email: userInfo.companyEmail || userInfo.email || '',
          phoneNumber: userInfo.contactNumber || '',
          company: refreshedCompany
        }));
        setCompanies(prev => ensureCompanyPresence(prev, refreshedCompany));
        // Re-fetch companies to include any newly created company values
        fetchCompanies();
      }
    } catch (err: any) {
      console.error('Failed to update profile:', err);
      setError(err.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5722] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading account information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-white border-b">
        <div className="px-6 py-4">
          <h1 className="text-xl font-bold">Account Settings</h1>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}
      {success && (
        <div className="max-w-3xl mx-auto bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md">
          {success}
        </div>
      )}

      {/* Account Details Section */}
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h2 className="text-lg font-medium">Account details</h2>
            <div className="mt-1 grid grid-cols-2 gap-x-6 text-sm">
              <div>
                <span className="text-gray-500">Customer ID</span>
                <p className="font-medium">{userData?.id || user?.id || 'N/A'}</p>
              </div>
              <div>
                <span className="text-gray-500">Login name</span>
                <p className="font-medium">{userData?.companyEmail || userData?.email || user?.email || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account Status Section */}
      <div className="max-w-3xl mx-auto bg-white rounded-lg shadow p-6">
        <div className="flex items-center space-x-4">
          <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-lg font-medium">Account status</h2>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-6">
          {/* Identity Verification */}
          <div>
            <h3 className="font-medium mb-2">Identity verification</h3>
            <p className="text-sm text-gray-600 mb-4">
              To protect the our platform, free credits can only be used to send messages to your own phone number, verify your identity to test our service. Top up your account to use other numbers.
            </p>
            <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200">
              Verify identity
            </button>
          </div>

          {/* Account Verification */}
          <div>
            <h3 className="font-medium mb-2">Account verification</h3>
            <p className="text-sm text-gray-600 mb-4">
              Having an unverified account puts limitations on some of Sendberry core functions, including how much SMS messages you can send per day. Your daily limit is 100 SMS.
            </p>
            <button className="bg-[#FF5722] text-white px-4 py-2 rounded-md hover:bg-[#F4511E] transition-colors duration-200">
              Lift limits
            </button>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="max-w-3xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Personal Information Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Personal Information</h2>
            
            <div className="grid grid-cols-2 gap-6">
              {/* First Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData({...formData, phoneNumber: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>
            </div>

            {/* Company - Full Width */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Company
              </label>
              <select
                value={formData.company}
                onChange={(e) => setFormData({...formData, company: e.target.value})}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
              >
                <option value="">Select a company</option>
                {companies.length > 0 ? (
                  companies.map((company) => (
                    <option key={company} value={company}>
                      {company}
                    </option>
                  ))
                ) : (
                  <option value="" disabled>No companies available</option>
                )}
              </select>
              {companies.length === 0 && (
                <p className="mt-1 text-xs text-gray-500">No companies found in the database. Companies will appear here once they are added to user profiles.</p>
              )}
            </div>
          </div>

          {/* Change Password Section */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-6">Change Password</h2>
            
            <div className="space-y-4">
              {/* Current Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Current Password
                </label>
                <input
                  type="password"
                  value={formData.currentPassword}
                  onChange={(e) => setFormData({...formData, currentPassword: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Password
                </label>
                <input
                  type="password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData({...formData, newPassword: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>

              {/* Confirm New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF5722] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => {
                setError(null);
                setSuccess(null);
                // Reset form to original values
                if (userData) {
                  setFormData({
                    firstName: userData.firstName || '',
                    lastName: userData.lastName || '',
                    email: userData.companyEmail || userData.email || '',
                    phoneNumber: userData.contactNumber || '',
                    company: userData.company || '',
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                  });
                }
              }}
              disabled={isSaving}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-6 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#F4511E] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountPage; 